// The boot both shell main.ts files spelled line for line (docs/design/shared-shell.md §4.5
// `boot.ts`; §5 B3 moved the invite link, the share chain and the session-event adapters out of
// gin's main.ts and backgammon's, C3 the ~290 lines around them into `bootShell`). Under
// web/shared/edge rather than the web/shared/ui the plan lists (§4.1 leaves the folder to this
// file) because it reaches past the ui zone's contract (web/shared/lib, the DOM edge and the clock
// fake, eslint.config.js zones): the invite readers (invite.ts), the share sheet (share.ts), the
// wake lock and Web Audio (fx.ts), the sessions' event types (web/shared/net), the browser net deps
// and localStorage. Each helper takes the page's objects injected (`location` and `history`,
// `navigator`), and `bootShell` takes them all in `cfg.page` (the document, the window, the
// navigator, the store and the clock), so boot.test.ts drives the whole boot over the page fake and
// main.ts passes the real ones. `Math.random` is read here (RNG_ALLOWED covers this folder), as the
// two main.ts files read it: the harness's `window.__rng` when installed, else the real one.
import type { Clock } from '../lib/clock.ts';
import { inviteUrl } from '../lib/invite.ts';
import type { RecentGame } from '../lib/recentGames.ts';
import type { Phrase } from '../lib/sound/phrase.ts';
import type { Result } from '../lib/result.ts';
import type { Rng } from '../lib/rng.ts';
import { badSoundFontMsg, isSoundFont, type SoundFontName } from '../lib/sound/fonts.ts';
import { isGuestFrame } from '../lib/protocol.ts';
import type { GuestEvents } from '../net/guest.ts';
import type { HostEvents } from '../net/host.ts';
import {
  aboutHtml,
  ruleFromHash,
  rulesListHtml,
  type Glossary,
  type RuleGroup,
  type RuleItem,
} from '../ui/glossary.ts';
import { fillNameInputs, fillP2NameInput, setCodeInput } from '../ui/home.ts';
import {
  flipped,
  gateOpen,
  playsOrientation,
  type Cue,
  type EphemeralOf,
  type FlipConfig,
  type FlipShell,
  type GateConfig,
  type GateState,
  type GuestContextOf,
  type GuestFrameOf,
  type HomeSnapshot,
  type HostContextOf,
  type HostFrameOf,
  type Intent,
  type ScreenId,
  type SeatOf,
  type ShellApp,
  type ShellTypes,
  type TimerId,
} from '../ui/shell.ts';
import type { ShellEffectDeps } from '../ui/shellEffects.ts';
import type { ReducerBlock } from '../ui/shellReducer.ts';
import {
  GATE_GO_ID,
  GATE_KEEP_ID,
  paintFlip,
  paintGate,
  paintSound,
  renderCopy,
  type ToastMarks,
} from '../ui/shellPaint.ts';
import { createTimers, createToaster, type Toast } from '../ui/toast.ts';
import { realClock } from './clock.ts';
import { shellFx, type CuePlayer, type CuePlayerDeps } from './cuePlayer.ts';
import {
  byId,
  listen,
  type DocumentLike,
  type Listenable,
  type PageLike,
  type StyleReaderLike,
} from './dom.ts';
import {
  createAudioCues,
  createWakeLock,
  vibrate,
  type AudioContextLike,
  type NavigatorLike,
  type WakeLock,
} from './fx.ts';
import { bindJargon, revealRule } from './glossary.ts';
import { joinCodeFrom, withoutJoin } from './invite.ts';
import { LANDSCAPE_PHONE, PORTRAIT_PHONE, watchMedia, type MediaQueryListLike } from './media.ts';
import {
  applyFrame,
  applyLayout,
  framed,
  probeAsked,
  renderProbe,
  watchFrame,
  watchLayout,
} from './screen.ts';
import { createOrientationLock, type OrientationLock } from './orientation.ts';
import { reducedMotion } from './motion.ts';
import { browserNetDeps } from './netDeps.ts';
import type { NetDeps } from './peer.ts';
import { shareText, type ShareNavigatorLike } from './share.ts';
import { createSampleCache } from './sound.ts';
import { createAudioUnlock, type UnlockDocumentLike, type UnlockNavigatorLike } from './unlock.ts';
import { browserStore, type Store } from './storage.ts';

// ---- the invite link ------------------------------------------------------------------------

/** The page's `location` and `history`, as `applyInviteLink` reads and rewrites them. */
export type InviteWindowLike = Readonly<{
  location: Readonly<{ search: string; pathname: string; hash: string }>;
  history: Readonly<{ replaceState: (data: null, unused: string, url: string) => void }>;
}>;

/**
 * An invite link (`?join=<code>`, docs/ARCHITECTURE.md "Documented test hooks"): the code goes
 * to the reducer once the home screen is up, which fills the join form and sits the guest down
 * as a tap on `#joinBtn` would (shell.ts `join/link`), and leaves the address bar, so a reload or
 * a bookmark of this page lands on the ordinary home screen (the other hooks, `?peer=` and
 * `?ice=`, stay). `bootShell` calls it right after `home/init`, so the remembered name is in the
 * App the reducer reads; `joinByLink` is its `join/link`.
 */
export const applyInviteLink = (
  win: InviteWindowLike,
  joinByLink: (code: string) => void,
): void => {
  const { location, history } = win;
  const join = joinCodeFrom(location.search);
  if (join !== null) {
    joinByLink(join);
    const query = withoutJoin(location.search);
    history.replaceState(
      null,
      '',
      `${location.pathname}${query === '' ? '' : `?${query}`}${location.hash}`,
    );
  }
};

// ---- the share chain ------------------------------------------------------------------------

export const INVITE_COPIED_MSG = 'Invite copied to clipboard';
/** `shareText`'s last resort: the code itself, for the player to read out. */
export const roomCodeMsg = (code: string): string => `Room code: ${code}`;
/** `shareCodeBtn`'s fallback toast lasts this long. */
export const SHARE_FALLBACK_MS = 4000;

export type ShareInviteOptions = Readonly<{
  /** The share sheet's title: the game's name as its legacy page spelled it. */
  title: string;
  code: string;
  /** The page's origin and path (`${location.origin}${location.pathname}`), the invite's base. */
  pageUrl: string;
  toast: Toast;
}>;

/**
 * The legacy handler's chain: the share sheet (a phone's OS menu), else the clipboard with a
 * toast (desktop), else the code itself. The invite is the link alone (`?join=<code>` on the
 * page's origin and path, so a fragment on this page never lands in it): no text beside it.
 */
export const shareInvite = (nav: ShareNavigatorLike, o: ShareInviteOptions): Promise<void> =>
  shareText(nav, { title: o.title, url: inviteUrl(o.code, o.pageUrl) }).then((outcome) => {
    if (outcome === 'copied') o.toast(INVITE_COPIED_MSG, null);
    else if (outcome === 'failed') o.toast(roomCodeMsg(o.code), SHARE_FALLBACK_MS);
  });

// ---- the sessions' events -------------------------------------------------------------------

/**
 * The intents a session raises: the same nine members of both games' `Intent` unions
 * (ui/state.ts), over the game's guest frame `G` and host frame `H`. A game's `dispatch` takes its
 * whole union, so it fits here as it is. `S` is the game's seat type (shell.ts `SeatOf<G>`), which
 * the two host intents carry only from the seated adapter (`sessionEvents(deps, { seats: true })`,
 * docs/design/n-seat-sessions.md §7); the default adapter's objects are the two-seat ones.
 */
export type SessionIntent<G, H, S extends number = number> =
  | Readonly<{ type: 'host/status'; text: string; stopPulse: boolean }>
  | Readonly<{ type: 'host/start'; code: string | null }>
  | Readonly<{ type: 'host/frame'; frame: G; seat?: S }>
  | Readonly<{ type: 'host/guestGone'; iceFailed: string | null; seat?: S }>
  | Readonly<{ type: 'guest/status'; text: string; stopPulse: boolean }>
  | Readonly<{ type: 'guest/connected' }>
  | Readonly<{ type: 'guest/frame'; frame: H }>
  | Readonly<{ type: 'guest/lost' }>
  | Readonly<{ type: 'persist' }>;

export type SessionEventDeps<G, H, S extends number = number> = Readonly<{
  dispatch: (intent: SessionIntent<G, H, S>) => void;
  toast: Toast;
  wakeLock: Pick<WakeLock, 'hold'>;
}>;

/** `seats`: the host's `frame` and `guestGone` intents carry the seat the session names (an N-seat game); off, they are the two-seat objects. */
export type SessionEventOptions = Readonly<{ seats?: boolean }>;

export type SessionEvents<G, H> = Readonly<{ host: HostEvents<G>; guest: GuestEvents<H> }>;

/**
 * The adapters main.ts hands its sessions (`events` in their deps): every event is an intent
 * through `dispatch` (`status` with `stopPulse` false unless the session says so), a toast through
 * `toast` (an undefined `ms` is the default duration), and the wake lock held. The session numbers
 * every seat (1 at capacity 2); with `seats` the two host intents carry it as the game's seat type.
 */
export const sessionEvents = <G, H, S extends number = number>(
  deps: SessionEventDeps<G, H, S>,
  options: SessionEventOptions = {},
): SessionEvents<G, H> => {
  const { dispatch, toast, wakeLock } = deps;
  const seated = options.seats === true;
  const host: HostEvents<G> = {
    status: (text, stopPulse = false) => {
      dispatch({ type: 'host/status', text, stopPulse });
    },
    toast: (message, ms) => {
      toast(message, ms ?? null);
    },
    holdWakeLock: () => {
      void wakeLock.hold();
    },
    persist: () => {
      dispatch({ type: 'persist' });
    },
    restart: (code) => {
      dispatch({ type: 'host/start', code });
    },
    // The session's seat is a number; the game's bag names the seats it seats (shell.ts `SeatOf`).
    frame: (frame, seat) => {
      dispatch(
        seated ? { type: 'host/frame', frame, seat: seat as S } : { type: 'host/frame', frame },
      );
    },
    guestGone: (iceFailed, seat) => {
      dispatch(
        seated
          ? { type: 'host/guestGone', iceFailed, seat: seat as S }
          : { type: 'host/guestGone', iceFailed },
      );
    },
  };
  const guest: GuestEvents<H> = {
    status: (text, stopPulse = false) => {
      dispatch({ type: 'guest/status', text, stopPulse });
    },
    toast: (message, ms) => {
      toast(message, ms ?? null);
    },
    holdWakeLock: () => {
      void wakeLock.hold();
    },
    persist: () => {
      dispatch({ type: 'persist' });
    },
    connected: () => {
      dispatch({ type: 'guest/connected' });
    },
    frame: (frame) => {
      dispatch({ type: 'guest/frame', frame });
    },
    lost: () => {
      dispatch({ type: 'guest/lost' });
    },
  };
  return { host, guest };
};

// ---- the boot ---------------------------------------------------------------------------------

/** A game's type bag with the store the edge builds (web/shared/edge/storage.ts): what `bootShell` reads of it. */
export type BootTypes = ShellTypes & Readonly<{ Store: Store }>;

/**
 * What the boot reads of an App: the font every cue plays in, my view (the hook's `legal()`), the
 * finished games (the hook's `recentGames()`) and, for a game that plays sideways, what the turn
 * gate reads (shell.ts `gateOpen`: the screen, the orientation, the dismissal, the Android lock).
 */
export type BootApp<G extends BootTypes> = Readonly<{
  shell: Readonly<{
    soundFont: SoundFontName;
    view: G['View'] | null;
    /** The engine state on this device (the host's or the phone's; null at a guest): the hook's `game()`. */
    game: G['State'] | null;
    recentGames: ReadonlyArray<RecentGame>;
  }> &
    GateState<G> &
    FlipShell<G>;
  /** The table's curtain (`ShellTypes.Table`): what the far seat's flip reads (shell.ts `flipped`). */
  table: Readonly<{ curtain: number | null }>;
}>;

/**
 * The page's `document` as the boot reads it: the paint's and the binders' view, its visibility,
 * and (optional, so the fakes need nothing) `createElement` for the audio unlock's silent loop.
 */
export type BootDocumentLike = PageLike &
  Readonly<{
    visibilityState: string;
    createElement?: (tag: 'audio') => HTMLAudioElement;
    /**
     * The Fullscreen API as the Android lock reads it (orientation.ts `FullscreenDocumentLike`;
     * docs/design/backgammon-landscape.md §5C): `documentElement.requestFullscreen` (the whole
     * document, since the curtain, the sheets and the toast are `#app`'s body siblings),
     * `exitFullscreen`, and `fullscreenElement`, null once a back gesture has left fullscreen. All
     * optional, so the fakes need nothing and an iPhone browser (no element fullscreen) fits.
     */
    documentElement?: Readonly<{
      requestFullscreen?: () => Promise<void>;
      /** The root's inline style, where the boot writes the frame's `--frame-corner-*` (screen.ts, dom.ts `setRootStyle`). */
      style?: Readonly<{ setProperty: (prop: string, value: string) => void }>;
    }>;
    exitFullscreen?: () => Promise<void>;
    fullscreenElement?: unknown;
  }>;

/** `fetch`'s answer as `fetchArrayBuffer` reads it. */
export type ResponseLike = Readonly<{
  ok: boolean;
  status: number;
  arrayBuffer: () => Promise<ArrayBuffer>;
}>;

/** The page's `window` as the boot reads it (the invite readers' `location` and `history` included). */
export type BootWindowLike = InviteWindowLike &
  StyleReaderLike &
  Readonly<{
    location: Readonly<{ origin: string }>;
    confirm: (message: string) => boolean;
    scrollTo: (x: number, y: number) => void;
    fetch: (url: string) => Promise<ResponseLike>;
    /** A seeded rng a harness installs before boot (docs/ARCHITECTURE.md "Documented test hooks"). */
    __rng?: Rng;
    AudioContext?: new () => unknown;
    webkitAudioContext?: new () => unknown;
    /**
     * `matchMedia('(pointer: coarse)')`: a phone or tablet, where sound starts muted
     * (sound-fonts.md §12); `matchMedia('(hover: none)')`: no pointer that hovers, the half of
     * `Ctx.canLock` that keeps a touchscreen laptop out; the two phone predicates of a game that
     * plays sideways (web/shared/edge/media.ts `watchMedia` on `PORTRAIT_PHONE` and
     * `LANDSCAPE_PHONE`, `cfg.shell` below); handed on through `BootCtx.matchMedia` for a game's
     * own watcher.
     */
    matchMedia?: (query: string) => MediaQueryListLike;
    /**
     * `screen.orientation.lock`, where the browser has one: read once at boot, with `(hover: none)`
     * above, as the reducer's `Ctx.canLock` (shell.ts: the rotation hint's device test). A function
     * on every Chromium, Android's and the desktop's alike (where it rejects), so the function
     * alone does not say "a phone"; absent on every iPhone browser, and no `screen` at all (the
     * boot test's window) says no as well. The DOM lib spells no `lock` (Safari has none, so it is
     * not baseline): read here as `unknown`, beside the `angle` the lib does spell, so the real
     * `Window` fits.
     */
    screen?: Readonly<{
      orientation?: Readonly<{ angle?: number; lock?: unknown; unlock?: () => void }>;
      /** The screen in CSS points (screen.ts `readDevice`: the device table's key, web/shared/lib/devices.ts). */
      width?: number;
      height?: number;
    }>;
    /** With `screen` and `getComputedStyle` (dom.ts `StyleReaderLike`: the insets off shell.css's `--frame-inset-*`), the device catalogue's inputs, the frame's corners and the probe's readout (screen.ts). */
    devicePixelRatio?: number;
    innerWidth?: number;
    innerHeight?: number;
    /** The frame watcher's two (screen.ts `watchFrame`): `resize`/`orientationchange`, and the visual viewport's `resize` as Safari's bar moves. */
    addEventListener?: Listenable['addEventListener'];
    visualViewport?: Listenable | null;
  }>;

/**
 * The `navigator` as the wake lock and the share chain read it. `vibrate` is not named: the DOM lib
 * types it over a mutable array and the edge (fx.ts `NavigatorLike`) over readonly patterns, so
 * `bootShell` widens the real object to the structural subset, as both main.ts files did.
 */
export type BootNavigatorLike = ShareNavigatorLike &
  UnlockNavigatorLike &
  Readonly<{ wakeLock?: NavigatorLike['wakeLock'] }>;

/** The page's objects, injected: main.ts passes the real ones, boot.test.ts the fakes. */
export type BootPage<G extends BootTypes> = Readonly<{
  doc: BootDocumentLike;
  win: BootWindowLike;
  nav: BootNavigatorLike;
  store: G['Store'];
  clock: Clock;
}>;

/**
 * The browser's page for `BootConfig.page`: `document`, `window`, `navigator`, localStorage
 * (storage.ts `browserStore`) and the real clock, what every main.ts spelled (docs/ARCHITECTURE.md
 * "Module boundaries": main.ts constructs the adapters). Typed over the bag the edge builds, so a
 * game whose `Store` is the edge's takes it as is; the boot test passes its fakes instead.
 */
export const browserPage = (): BootPage<BootTypes> => ({
  doc: document,
  win: window,
  nav: navigator,
  store: browserStore(),
  clock: realClock,
});

/**
 * One engine action through the game's reducer, `{ type: 'act', action }`: every game spells this
 * intent (its `act` case checks the turn and applies the action), so the hook's `act` is the boot's.
 * `BootConfig` refuses a game whose `Intent` lacks it (`ActsOn`).
 */
export type ActIntent<G extends BootTypes> = Readonly<{ type: 'act'; action: G['Action'] }>;
type ActsOn<G extends BootTypes> =
  ActIntent<G> extends G['Intent']
    ? unknown
    : Readonly<{ "G['Intent'] must include ActIntent<G>: the hook's act dispatches it": never }>;
/** `bootShell` lets `deps` be left out when `Ex` asks for nothing (`object`, six games); gin's `GinDeps` must be passed. */
type DepsOf<Ex extends object> = object extends Ex ? unknown : Readonly<{ deps: Ex }>;

/** A session as the boot drives it (web/shared/net): `kind` tells the host from the guest, `send` takes its side's frame (and, for a host, the one seat to send to; every open channel when absent). */
export type SessionLike<K extends 'host' | 'guest', F> = Readonly<{
  kind: K;
  send: (frame: F, seat?: number) => void;
  close: () => void;
}>;
/** A host session's deps over the game's bag; its `read` returns the shell's host context, `seats` among its fields for an N-seat codec's welcome. */
export type HostDepsOf<G extends BootTypes> = NetDeps &
  Readonly<{ read: () => HostContextOf<G>; events: HostEvents<GuestFrameOf<G>> }>;
export type GuestDepsOf<G extends BootTypes> = NetDeps &
  Readonly<{ read: () => GuestContextOf; events: GuestEvents<HostFrameOf<G>> }>;
/** The host session's options less the game: `capacity`, `waiting` and `names` ride only for an N-seat room (shell.ts `startHost` effect). */
export type HostOptionsOf = Readonly<{
  code: string;
  attempt: number;
  resume: boolean;
  capacity?: number;
  waiting?: string;
  names?: ReadonlyArray<string | null>;
}>;
export type GuestOptionsOf = Readonly<{ code: string; attempt: number }>;

/** What the boot hands `fx` (cuePlayer.ts `shellFx` by default): the shared player's deps less the table and the persist it supplies. */
export type FxDepsOf<G extends BootTypes> = Omit<CuePlayerDeps<Cue<G>>, 'cues' | 'persist'> &
  Readonly<{ store: G['Store'] }>;

/** What the boot built, for the game's own hook members and effect adapters (`cfg.hooks`) and for main.ts. */
export type BootCtx<G extends BootTypes, App extends BootApp<G>> = Readonly<{
  dispatch: (intent: Intent<G>) => void;
  /** The current App (a function, so a reader always sees the latest record). */
  app: () => App;
  toast: Toast;
  fx: CuePlayer<Cue<G>>;
  store: G['Store'];
  now: () => number;
  rng: Rng;
  /** `readHome` after the stored sound font (and the game's own `hooks.home` guard) is checked: the `home/init` snapshot. */
  homeSnapshot: () => HomeSnapshot<G>;
  /**
   * The window's `matchMedia`, bound to it (a detached copy throws "Illegal invocation"), for a
   * game's `hooks.bind` to watch a query through media.ts; absent on a page without one.
   */
  matchMedia?: (query: string) => MediaQueryListLike;
}>;

/** A game's copy tables: the rules (flat, or grouped as hive's), the About paragraphs and the glossary that links the jargon in both. */
export type BootCopy = Readonly<{
  rules: ReadonlyArray<RuleItem> | ReadonlyArray<RuleGroup>;
  about?: ReadonlyArray<string>;
  glossary: Glossary;
}>;

/** The copy's markup as shellPaint.ts `renderCopy` takes it: the rules list and, when the game has it, the About copy. */
export const copyHtml = (copy: BootCopy): Readonly<{ rules: string; about?: string }> => ({
  rules: rulesListHtml(copy.rules, copy.glossary),
  ...(copy.about === undefined ? {} : { about: aboutHtml(copy.about, copy.glossary) }),
});

/**
 * Everything the two main.ts files disagreed on, spelled by each (§4.5): `G` the game's type bag,
 * `App` its App (the boot reads `shell.soundFont` and `shell.view` of it), `Ex` the effect adapters
 * it has beside the shell's (gin's Score Counter and clipboard; backgammon none).
 */
export type BootConfig<G extends BootTypes, App extends BootApp<G>, Ex extends object> = Readonly<{
  /** The page's objects (`browserPage()` in a browser; the boot test's fakes). */
  page: BootPage<G>;
  game: Readonly<{
    /** The documented test hook's property on the window: `__gin`, `__backgammon` (tools/games.ts REGISTRY `hook`). */
    hook: string;
    /** The share sheet's title: the game's name as its legacy page spelled it. */
    title: string;
    /** The PeerJS log level the page's legacy set (REGISTRY `debug`; e2e `expectPeerOptions` pins each). */
    debug: number;
  }>;
  /**
   * Left out by every game: sound starts on by prefs.ts `soundPref.enabled` over
   * `config.prefs.sound` (a stored `on`/`off` wins; `fallback` is what no stored preference counts
   * as, `false` on a coarse-pointer device, sound-fonts.md §12). The boot test passes its own
   * reader to log the fallback. The sound-font key is the store's (`config.prefs.keys.soundFont`).
   */
  sound?: Readonly<{
    enabled?: (store: G['Store'], fallback: boolean) => boolean;
  }>;
  /**
   * The game's reducer over its App: `initialApp`, `reduce`, `runEffect`, `readHome` and the two
   * session contexts (shellReducer.ts `ReducerBlock`). A shell game passes
   * `shellReducer(cfg, table)`'s result (`ui/state.ts` `reducer`); the boot test's fake spells its own.
   */
  reducer: ReducerBlock<G, App, Ex>;
  paint: Readonly<{
    paint: (doc: PageLike, app: App) => void;
    bindAll: (doc: PageLike, dispatch: (intent: Intent<G>) => void) => void;
    /** The speaker button's paint: shellPaint.ts `paintSound` unless the game paints its own. */
    paintSound?: (doc: DocumentLike, enabled: boolean) => void;
    /** The classes a game puts on the toast for a message (backgammon's `hit`); none for gin. */
    toastMarks?: (message: string) => ToastMarks;
    /**
     * The three input writes the paint does not own: the shell's (web/shared/ui/home.ts
     * `fillNameInputs`, `fillP2NameInput`, `setCodeInput`) unless the game fills more, as gin fills
     * the Score Counter's inputs too. `isDefault` is the fill's `default` mark (shell.ts), painted
     * as `data-default` so the input clears on the first tap.
     */
    fillName?: (doc: DocumentLike, name: string, isDefault: boolean) => void;
    fillP2Name?: (doc: DocumentLike, name: string, isDefault: boolean) => void;
    setCode?: (doc: DocumentLike, value: string) => void;
  }>;
  /**
   * The game's copy as the tables it keeps (ui/rules.ts `RULES_ITEMS` or `RULE_GROUPS`,
   * `ABOUT_PARAGRAPHS`, `GLOSSARY`): the boot renders the rules (glossary.ts `rulesListHtml`) into
   * both rules slots and the About copy (`aboutHtml`) into its slot before the game's
   * `hooks.render` (shellPaint.ts `renderCopy`); absent for a page that renders its own
   * (backgammon keys both slots on its ruleset).
   */
  copy?: BootCopy;
  /**
   * What the boot reads of the game's shell config (its `ShellConfig` constant, ui/state.ts): the
   * cue table and the `sound` preference the default `fx` plays and persists through.
   */
  config: Readonly<{
    cues: Readonly<{ table: Readonly<Record<Cue<G>, Phrase>> }>;
    prefs: Readonly<{
      /** The store's sound-font key (prefs.ts `shellStore` `keys`): a stored value that names no font is logged under it and dropped. */
      keys: Readonly<{ soundFont: string }>;
      sound: Readonly<{
        read: (store: G['Store']) => Result<'on' | 'off', unknown>;
        write: (store: G['Store'], state: 'on' | 'off') => unknown;
      }>;
    }>;
    /** The seat range of an N-seat game (shell.ts `ShellConfig.seats`): with it, the sessions are seated (`net.seats`). */
    seats?: Readonly<{ min: number; max: number }>;
  }>;
  /**
   * The cue player over the boot's deps: cuePlayer.ts `shellFx(config.cues.table, config.prefs.sound)`
   * unless a game passes its own (the boot test's fake records the deps).
   */
  fx?: (deps: FxDepsOf<G>) => CuePlayer<Cue<G>>;
  net: Readonly<{
    Host: new (deps: HostDepsOf<G>, opts: HostOptionsOf) => SessionLike<'host', HostFrameOf<G>>;
    Guest: new (
      deps: GuestDepsOf<G>,
      opts: GuestOptionsOf,
    ) => SessionLike<'guest', GuestFrameOf<G>>;
    /** Which side of the wire a `send` effect's frame belongs to: by default the shared predicate (web/shared/lib/protocol.ts `isGuestFrame`: `join` and `action` are a guest's); the boot test passes its own. */
    isGuestFrame?: (frame: HostFrameOf<G> | GuestFrameOf<G>) => frame is GuestFrameOf<G>;
    /**
     * The game's protocol.ts `isEphemeral`, for a game with an ephemeral lane (`ShellTypes.Ephemeral`):
     * a `send` of that frame goes out on whichever session is open, host or guest. Absent, no frame is one.
     */
    isEphemeral?: (frame: HostFrameOf<G> | GuestFrameOf<G>) => frame is EphemeralOf<G>;
    /** A table of more than two seats: the host's `frame`/`guestGone` intents carry the seat (`sessionEvents(deps, { seats: true })`). By default, whether `config.seats` is set. */
    seats?: boolean;
  }>;
  /** The engine's legal actions for a view (the hook's `legal()`). */
  legal: (view: G['View']) => ReadonlyArray<G['Action']>;
  /**
   * The game's shell config, for what the boot reads of it (shell.ts `GateConfig`: a `ShellConfig`
   * fits): with `orientation: 'landscape'` the boot watches the two phone predicates into the
   * reducer (`viewport/portrait`, `viewport/landscape`) and paints the turn gate after every paint
   * (shellPaint.ts `paintGate` over `gateOpen`). Absent, or without the orientation: nothing of it.
   * With it, whatever the orientation, the far seat's flip is painted too (shellPaint.ts
   * `paintFlip` over `flipped`, `FlipConfig`: the game's `local.holder` hook).
   */
  shell?: GateConfig<G> & FlipConfig<G>;
  hooks?: Readonly<{
    /** Before every home read: gin drops a stored card back that names no preset. */
    home?: (store: G['Store']) => void;
    /** Before the binders: the static markup and screens a game renders first (gin: the rules, the About copy, the sandbox presets, the Score Counter). */
    render?: (ctx: BootCtx<G, App>) => void;
    /** After the binders: gin's `scorer.bind()`. */
    bind?: (ctx: BootCtx<G, App>) => void;
    /** The game's own members on the test hook, after the shared ones (`act`, `view`, `setup` and `legal` among them): uno's `playable`, gin's sandbox. A member spelled here replaces the shared one of that name. */
    hook?: (ctx: BootCtx<G, App>) => Readonly<Record<string, unknown>>;
  }>;
  /** The game's own effect adapters beside the shell's (`Ex`): gin's `scorer` and `copy`; left out by a game with none (`DepsOf`). */
  deps?: Ex;
}>;

/** prefs.ts `soundPref.enabled` over the shell's pref: a stored `on`/`off` wins, else `fallback`. */
const soundEnabledOf =
  <St>(pref: Readonly<{ read: (store: St) => Result<'on' | 'off', unknown> }>) =>
  (store: St, fallback: boolean): boolean => {
    const stored = pref.read(store);
    return stored.ok ? stored.value === 'on' : fallback;
  };

/** The game's `act` intent as the reducer's `Intent<G>`: `ActsOn<G>` has checked the membership the type system cannot see through the bag. */
const actIntent = <G extends BootTypes>(action: G['Action']): Intent<G> =>
  ({ type: 'act', action }) as Intent<G>;

/** `SoundDeps.fetchBuffer` (sound.ts) over the page's `fetch`: the bytes, or a rejection naming the status and the URL. */
export const fetchArrayBuffer =
  (win: Readonly<{ fetch: BootWindowLike['fetch'] }>) =>
  (url: string): Promise<ArrayBuffer> =>
    win.fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${String(r.status)} ${url}`);
      return r.arrayBuffer();
    });

/**
 * The boot (docs/ARCHITECTURE.md "Module boundaries": it constructs the adapters and injects
 * them; no logic): the wake lock, Web Audio, the cue player, the toaster and the named timers, the
 * browser net deps, `dispatch` with the no-repaint rule, the sessions' events, the shell's effect
 * adapters, the binders, the warm-on-first-gesture and visibility listeners, the documented test
 * hook, then `home/init`, the invite link and the rule deep link, in the order both main.ts files
 * ran them. Returns what it built, so main.ts can reach the dispatch (nothing does today).
 */
export const bootShell = <
  G extends BootTypes,
  App extends BootApp<G> = ShellApp<G> & BootApp<G>,
  Ex extends object = object,
>(
  cfg: BootConfig<G, App, Ex> & DepsOf<Ex> & ActsOn<G>,
): BootCtx<G, App> => {
  const { doc, win, nav, store, clock } = cfg.page;
  // The sound font (docs/design/sound-fonts.md §6): a value the console left in storage that names
  // no preset is logged and dropped before every home read, so the default stands and a reload
  // logs it once; the game's own guard (gin's card back, `hooks.home`) runs first, as it did. The
  // key is the store's own (prefs.ts `shellStore` built it from the game's prefix).
  const fontKey = cfg.config.prefs.keys.soundFont;
  const homeSnapshot = (): HomeSnapshot<G> => {
    cfg.hooks?.home?.(store);
    const storedFont = store.readText(fontKey);
    if (storedFont.ok && !isSoundFont(storedFont.value)) {
      console.error(badSoundFontMsg(fontKey, storedFont.value));
      store.remove(fontKey);
    }
    return cfg.reducer.readHome(store);
  };
  // The seeded rng a harness installs before boot, read before anything draws so a seeded opening
  // roll is the seeded one (backgammon-board.md R27), else the real one.
  const rng: Rng = win.__rng ?? Math.random;
  // A `send` effect's frame goes out on the side it belongs to: `join` and `action` are a guest's
  // (the shared predicate), the rest a host's; a game's own predicate when it passes one.
  const isGuest: (frame: HostFrameOf<G> | GuestFrameOf<G>) => frame is GuestFrameOf<G> =
    cfg.net.isGuestFrame ?? isGuestFrame;
  const now = (): number => clock.now();
  // The DOM lib types `vibrate` over a mutable array and `AudioNode.connect` over full nodes; the
  // edge reads readonly patterns and calls the structural subset, so the real objects are widened.
  const navLike = nav as NavigatorLike;
  const wakeLock: WakeLock = createWakeLock(navLike);
  const shell = cfg.shell;
  /** The shell config of a game that plays one way (shell.ts `playsOrientation`: sideways or upright), or null for a game that plays either way or holds no shell config: the watchers, the gate and the lock's target follow it. */
  const gated = shell === undefined || playsOrientation(shell) === null ? null : shell;
  const plays = gated === null ? null : playsOrientation(gated);
  // The Android lock (orientation.ts; docs/design/backgammon-landscape.md §5C) over the document
  // and the screen, holding the way the game plays (sideways unless the config says upright); a
  // window without a `screen` (the boot test's) gets one that does nothing.
  const orientationLock: OrientationLock = createOrientationLock(
    doc,
    win.screen ?? {},
    plays ?? 'landscape',
  );
  const AudioCtor = win.AudioContext ?? win.webkitAudioContext;
  // A phone starts muted (the owner, 2026-09-25: "start muted on mobile so tapping the unmute is
  // what enables sound"; sound-fonts.md §12): iPhone Safari only lets audio start inside a tap
  // it counts, and the speaker button's tap is one. A remembered preference still wins, and a
  // page without `matchMedia` (the boot test's window) keeps the desktop default, on.
  const coarsePointer = win.matchMedia?.('(pointer: coarse)').matches === true;
  const audio = createAudioCues({
    makeContext: AudioCtor === undefined ? undefined : () => new AudioCtor() as AudioContextLike,
    enabled: (cfg.sound?.enabled ?? soundEnabledOf(cfg.config.prefs.sound))(store, !coarsePointer),
  });
  // The silent-switch unlock (unlock.ts): run inside the tap that turns sound on, and on the first
  // gesture over a page whose sound is already on.
  // The DOM lib types `appendChild` over full nodes; the unlock appends the one element it made,
  // so the body is widened to the structural subset, as `nav` is above.
  const unlock = createAudioUnlock(
    {
      ...(doc.createElement === undefined ? {} : { createElement: doc.createElement.bind(doc) }),
      body: doc.body as unknown as UnlockDocumentLike['body'],
    },
    nav,
  );

  let app: App = cfg.reducer.initialApp;
  let session: SessionLike<'host', HostFrameOf<G>> | SessionLike<'guest', GuestFrameOf<G>> | null =
    null;
  /** The reducer's named timers (the Play tab's long press; backgammon's shake and R14 beat); arming one again restarts it. */
  const timers = createTimers<TimerId<G>>(clock);
  /** The legacy `toast(msg, ms)` with its 2.6 s default; a new toast restarts the one hide timer (backgammon's Kapará toast wears `hit`). */
  const toast = createToaster(doc, clock, undefined, cfg.paint.toastMarks);
  const paintSoundBtn = cfg.paint.paintSound ?? paintSound;

  const createFx = cfg.fx ?? shellFx(cfg.config.cues.table, cfg.config.prefs.sound);
  const fx = createFx({
    audio,
    // The sample seam (docs/design/sound-fonts.md §3): a document-relative URL, so both origins serve it.
    sound: { fetchBuffer: fetchArrayBuffer(win), cache: createSampleCache() },
    vibrate: (pattern) => {
      vibrate(navLike, pattern);
    },
    store,
    onToggle: (enabled) => {
      // Still inside the speaker button's tap (`toggle` runs synchronously from the click): the
      // one gesture an iPhone lets the media unlock and the context's resume ride on.
      if (enabled) unlock.unlock();
      paintSoundBtn(doc, enabled);
    },
  });

  // PeerJS log level as the legacy page set it (e2e expectPeerOptions pins it); the shared deps
  // (web/shared/edge/netDeps.ts) read the ?peer= hook and wake the sessions on visibility/online.
  const netDeps: NetDeps = browserNetDeps({ search: win.location.search, debug: cfg.game.debug });

  // The device can lock its rotation (shell.ts `Ctx.canLock`: the rotation hint's test, the
  // Android lock's guard and the gate's "Go sideways"): a fact about the browser, read once so
  // every step's ctx and every paint agree. The function alone is desktop Chromium's too (where
  // it rejects), and `LANDSCAPE_PHONE`'s `any-pointer: coarse` takes a touchscreen laptop in a
  // short window for a phone, so the device must also have no pointer that hovers: a laptop's
  // trackpad does, a phone's finger does not, and it is never told Android's gesture. A window
  // without `matchMedia` cannot say, and says no.
  const canLock =
    typeof win.screen?.orientation?.lock === 'function' &&
    win.matchMedia?.('(hover: none)').matches === true;

  const repaint = (): void => {
    cfg.paint.paint(doc, app);
    // The shared half of the paint for a game that plays one way (docs/design/shared-shell.md
    // "Playing sideways"): the turn gate over the game's own, from the App alone, its "Go
    // sideways" (or "Go upright") shown where the device can lock; the words are the markup's.
    if (gated !== null) paintGate(doc, gateOpen(app.shell, gated), canLock);
    // The far seat's flip (shell.ts `flipped`): the body's `data-flip`, for any game whose config the boot holds.
    if (shell !== undefined) paintFlip(doc, flipped(app, shell));
  };

  const dispatch = (intent: Intent<G>): void => {
    const step = cfg.reducer.reduce(app, intent, {
      rng,
      now,
      reducedMotion: reducedMotion(),
      canLock,
    });
    // The paint is a function of the App, so an unchanged App needs none. This matters on a
    // card's or a checker's pointerdown: a repaint would replace the element under the pointer,
    // and the browser would then drop the click that was to follow.
    const changed = step.app !== app;
    app = step.app;
    step.effects.forEach((effect) => {
      cfg.reducer.runEffect(app, effect, deps);
    });
    if (changed) repaint();
  };

  // The sessions' events as intents, toasts and the wake lock.
  const { host: hostEvents, guest: guestEvents } = sessionEvents<
    GuestFrameOf<G>,
    HostFrameOf<G>,
    SeatOf<G>
  >({ dispatch, toast, wakeLock }, { seats: cfg.net.seats ?? cfg.config.seats !== undefined });

  const deps: ShellEffectDeps<G> & Ex = {
    store,
    toast,
    fx: (what, font) => {
      if (typeof what === 'string') fx.play(what, font);
      else fx.playPhrases(what, font);
    },
    wakeLock: (hold) => {
      if (hold) void wakeLock.hold();
      else wakeLock.drop();
    },
    orientationLock: (hold) => {
      // Inside the tap that stepped it: fullscreen, then the landscape lock. The reducer marked
      // the lock held as it emitted the effect; an attempt that did not take (desktop Chromium's
      // `lock` rejects, a tablet may refuse) is reported back so the mark drops, the rotation hint
      // is due again and the next tap tries once more.
      if (hold)
        void orientationLock.hold().then((held) => {
          if (!held) dispatch({ type: 'fullscreen/lost' });
        });
      else orientationLock.drop();
    },
    net: {
      // An N-seat room's terms (`room`) join the options; a two-seat game's options are the literal they were.
      startHost: (code, attempt, resume, room) => {
        session = new cfg.net.Host(
          { ...netDeps, read: () => cfg.reducer.hostContextOf(app), events: hostEvents },
          { code, attempt, resume, ...room },
        );
      },
      startGuest: (code, attempt) => {
        session = new cfg.net.Guest(
          { ...netDeps, read: () => cfg.reducer.guestContextOf(app), events: guestEvents },
          { code, attempt },
        );
      },
      send: (frame, seat) => {
        if (session === null) return;
        if (cfg.net.isEphemeral?.(frame) === true) {
          session.send(frame);
          return;
        }
        if (session.kind === 'host') {
          if (!isGuest(frame)) session.send(frame, seat);
        } else if (isGuest(frame)) session.send(frame);
      },
      close: () => {
        session?.close();
      },
    },
    confirm: (message) => win.confirm(message),
    scrollTop: () => {
      win.scrollTo(0, 0);
    },
    timers: {
      start: (id, ms, then) => {
        timers.start(id, ms, () => {
          dispatch(then);
        });
      },
      cancel: timers.cancel,
    },
    toggleSound: () => {
      fx.toggle(app.shell.soundFont);
    },
    share: (code) => {
      // The share sheet, else the clipboard with a toast, else the code itself (`shareInvite` above).
      void shareInvite(nav, {
        title: cfg.game.title,
        code,
        pageUrl: `${win.location.origin}${win.location.pathname}`,
        toast,
      });
    },
    revealRule: (slot, rule) => {
      revealRule(doc, slot, rule);
    },
    page: {
      fillName: (name, isDefault) => {
        (cfg.paint.fillName ?? fillNameInputs)(doc, name, isDefault);
      },
      fillP2Name: (name, isDefault) => {
        (cfg.paint.fillP2Name ?? fillP2NameInput)(doc, name, isDefault);
      },
      setCode: (value) => {
        (cfg.paint.setCode ?? setCodeInput)(doc, value);
      },
    },
    dispatch: (intent) => {
      dispatch(intent);
    },
    // `DepsOf<Ex>` let `deps` be left out only where `{}` is a whole `Ex`.
    ...(cfg.deps ?? ({} as Ex)),
  };

  const matchMedia = win.matchMedia?.bind(win);
  const ctx: BootCtx<G, App> = {
    dispatch,
    app: () => app,
    toast,
    fx,
    store,
    now,
    rng,
    homeSnapshot,
    ...(matchMedia === undefined ? {} : { matchMedia }),
  };

  // The screen frame's corners (docs/design/screen-frame.md §4; backgammon-board.md §3.6): the
  // display's radius from the device catalogue at each corner the page provably reaches, 0 at each
  // a browser bar owns, written on the root as the four `--frame-corner-*` over shell.css's `env()`
  // fallback, and again as the bar shows or hides, the phone turns or fullscreen comes and goes
  // (screen.ts `watchFrame`); a page without a frame (gin, briscola) reads nothing of them.
  // `?probe=1` (docs/ARCHITECTURE.md "Documented test hooks") draws the same numbers on the page.
  if (framed(doc)) {
    applyFrame(doc, win);
    watchFrame(doc, win, () => {
      applyFrame(doc, win);
    });
  }
  // The layout bucket (docs/design/layout-buckets.md; web/shared/lib/layout.ts): the viewport and
  // the pointer into one of seven names on `<body data-layout>`, for every page (a theme lays its
  // screens out per bucket: `body[data-layout="phone-sideways"] .table { ... }`), and again on
  // every resize and turn of the phone (screen.ts `watchLayout`). A window without a viewport (the
  // boot test's) writes nothing, so a forced attribute stands.
  applyLayout(doc, win);
  watchLayout(win, () => {
    applyLayout(doc, win);
  });
  if (probeAsked(win.location.search)) renderProbe(doc, win);
  if (cfg.copy !== undefined) renderCopy(doc, copyHtml(cfg.copy));
  cfg.hooks?.render?.(ctx);
  cfg.paint.bindAll(doc, dispatch);
  // A tap on jargon in the About copy or in a rule (docs/design/glossary-links.md) shows that rule.
  bindJargon(doc, (rule) => {
    dispatch({ type: 'rules/show', rule });
  });
  cfg.hooks?.bind?.(ctx);
  // A game that plays one way (sideways or upright): the phone's orientation into the App, now and on
  // every turn of the phone (media.ts `watchMedia` reports nothing on a page without `matchMedia`), and the
  // gate's one live control, "Play upright" (shellPaint.ts `GATE_KEEP_ID`), so a game's render.ts
  // binds nothing for the gate; `byId`, not `listenId`, because a sideways page without the gate
  // markup (a story, a test page) has no button to bind.
  if (gated !== null) {
    watchMedia(ctx, PORTRAIT_PHONE, (portrait) => {
      dispatch({ type: 'viewport/portrait', portrait });
    });
    watchMedia(ctx, LANDSCAPE_PHONE, (landscape) => {
      dispatch({ type: 'viewport/landscape', landscape });
    });
    const keep = byId(doc, GATE_KEEP_ID);
    if (keep !== null) {
      listen(keep, 'click', () => {
        dispatch({ type: 'gate/keep' });
      });
    }
    // "Go sideways" (painted only where `canLock`): the Android lock from the gate's own tap.
    const go = byId(doc, GATE_GO_ID);
    if (go !== null) {
      listen(go, 'click', () => {
        dispatch({ type: 'gate/turn' });
      });
    }
    // The lock ends with fullscreen (a back gesture runs the spec's fully-unlock steps), and
    // nothing re-locks without a tap: the App learns of the loss here, so the next tap re-enters
    // and the rotation hint, silent while the lock was held, is due again. Entering fullscreen
    // fires the same event with the element set, which is no loss.
    doc.addEventListener('fullscreenchange', () => {
      if ((doc.fullscreenElement ?? null) === null) dispatch({ type: 'fullscreen/lost' });
    });
  }
  paintSoundBtn(doc, fx.enabled());
  // Browsers only let audio start after a user gesture: warm the context on the first tap, and
  // the table's samples in the App's font with it (cuePlayer.ts `warm`), so no phrase waits.
  // The four are the gestures WebKit counts as activation (sound-fonts.md §12): `pointerdown`
  // for the desktop's first click, `touchend` and `click` for a phone (a tap that scrolls never
  // clicks, its `touchend` still counts), `keydown` for a keyboard; none is passive and none
  // awaits before `resume()`, so the call lands inside the gesture. Each also plays the silent
  // unlock loop when sound is on, so the ringer switch stops muting a page whose sound was
  // remembered on; while muted, nothing is made until the speaker button's tap (`onToggle`).
  ['pointerdown', 'touchend', 'click', 'keydown'].forEach((event) => {
    doc.addEventListener(event, () => {
      if (!fx.enabled()) return;
      fx.warm(app.shell.soundFont);
      unlock.unlock();
    });
  });
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState !== 'visible') return;
    // Back from the background: WebKit leaves the context `interrupted`; the warm asks it to resume
    // (allowed outside a gesture once the interruption ended, and asked again on the next tap).
    fx.warm(app.shell.soundFont);
    dispatch({ type: 'visible' });
  });

  // The test/debug hook (docs/ARCHITECTURE.md "Documented test hooks"): read-only state, actions
  // through the reducer. `app` is a getter so a reader always sees the current record; the game's
  // own members (`hooks.hook`: gin's sandbox, card back and layoffs, uno's `playable`) follow.
  const hook: Readonly<Record<string, unknown>> = {
    get app(): App {
      return app;
    },
    dispatch,
    render: () => {
      dispatch({ type: 'render' });
    },
    showScreen: (screen: ScreenId<G>) => {
      dispatch({ type: 'screen/show', screen });
    },
    initHome: () => {
      dispatch({ type: 'home/init', home: homeSnapshot() });
    },
    fx,
    /** One engine action through the reducer (`ActIntent`): what every e2e driver plays. */
    act: (action: G['Action']): void => {
      dispatch(actIntent<G>(action));
    },
    /** My seat's view, null before a game. */
    view: (): G['View'] | null => app.shell.view,
    /** The engine state on this device (the host's or the phone's), null at a guest or before a game: what a driver reads past the view. */
    game: (): G['State'] | null => app.shell.game,
    /** A position for e2e and stories: the pass-and-play game's state replaced (shell.ts `position/load`, decoded by the engine). */
    setup: (state: unknown): void => {
      dispatch({ type: 'position/load', state });
    },
    /** The engine's legal actions for my view. */
    legal: (): ReadonlyArray<G['Action']> =>
      app.shell.view === null ? [] : cfg.legal(app.shell.view),
    /** The sound font, from the console for now (docs/design/sound-fonts.md §6): a font plays from now on and is remembered; anything else is logged and refused. */
    soundFont: (name: string): void => {
      if (!isSoundFont(name)) {
        console.error(badSoundFontMsg(fontKey, name));
        return;
      }
      dispatch({ type: 'soundFont/set', font: name });
    },
    soundFontName: (): string => app.shell.soundFont,
    /** The finished games this device remembers, newest first (web/shared/lib/recentGames.ts): what the history sheet lists. */
    recentGames: (): ReadonlyArray<RecentGame> => app.shell.recentGames,
    ...cfg.hooks?.hook?.(ctx),
  };
  // `window.__gin = { … }` as the legacy page wrote it: the one write the boot makes on the window.
  (win as unknown as Record<string, unknown>)[cfg.game.hook] = hook;

  dispatch({ type: 'home/init', home: homeSnapshot() });
  // An invite link (`?join=<code>`): the guest is sat down now that the home screen is up, and the
  // code is out of the address bar (`applyInviteLink` above).
  applyInviteLink(win, (code) => {
    dispatch({ type: 'join/link', code });
  });
  // A waiting room this device opened a moment ago (a refresh, a tab the phone discarded) comes
  // back by itself (shell.ts `resume/auto`; docs/design/lobby-resume.md D4): after the invite
  // link, so a link to another room wins and a link to this room has already resumed hosting.
  dispatch({ type: 'resume/auto' });
  // A rule deep link (`#rule-<id>`, docs/design/glossary-links.md §1): the Rules tab, scrolled to
  // that rule. The hash stays, so the link can be copied from the address bar.
  const rule = ruleFromHash(win.location.hash);
  if (rule !== null) dispatch({ type: 'rules/show', rule });
  return ctx;
};
